'use client';

import React, { useState } from 'react';
import { Search, Brain, Upload, FileText, Link2, DollarSign, ArrowRight } from "lucide-react";
import Image from 'next/image';
import Link from 'next/link';

const Whatis = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const features = [
    {
      id: 1,
      icon: Search,
      title: "Stop Guessing Keywords",
      description: "Get Real Competitor Data in Seconds",
      image: "/images/screenshots/seozast-analysis.png",
      alt: "Seozast keyword analysis dashboard",
      testimonial: {
        quote: "This is an absolute game-changer for indie makers and content creators! The keyword clustering + autocomplete tools are super intuitive. Love how it cuts through the noise and helps me focus on topics that truly matter.",
        author: "droid HZ",
        source: "Fazier"
      }
    },
    {
      id: 2,
      icon: Brain,
      title: "Get Found by ChatGPT & Copilot",
      description: "Auto-indexed in Bing for AI Search",
      image: "/images/screenshots/seozast-ai-optimization.png",
      alt: "Seozast AI optimization interface",
      testimonial: {
        quote: "SEOZast solved some of our SEO complexity using it as a quick insight alternative and also backed with other SEO tools for content creation.",
        author: "Ajay Sahoo",
        source: "Product Hunt"
      }
    },
    {
      id: 3,
      icon: FileText,
      title: "Real-Time Analytics",
      description: "See Who's Reading Your Content Right Now",
      image: "/images/screenshots/seozast-analytics.png",
      alt: "Seozast real-time analytics dashboard",
      testimonial: {
        quote: "The SEO dashboard with keyword editing and AI content generation saves me so much time. The internal linking and built-in image generation really help with scaling content fast.",
        author: "Nagasamanvitha K",
        source: "Fazier"
      }
    },
    {
      id: 4,
      icon: Upload,
      title: "One-Click GitHub Deploy",
      description: "Publish to Webflow, Wordpress, Shopify Or Export to Any Format",
      image: "/images/screenshots/seozast-publishing.png",
      alt: "Seozast publishing and export formats interface",
      testimonial: {
        quote: "I love the 1-click GitHub publishing and the ability to manage multiple projects in one place. Highly recommend for anyone looking to grow traffic without heavy manual effort!",
        author: "Nagasamanvitha K",
        source: "Fazier"
      }
    },
    {
      id: 5,
      icon: Link2,
      title: "Auto Internal Linking",
      description: "Build Authority Without Busywork",
      image: "/images/screenshots/seozast-backlinks.png",
      alt: "Seozast backlink optimization interface",
      testimonial: {
        quote: "The internal linking and built-in image generation really help with scaling content fast. Perfect for indie hackers and startups like mine.",
        author: "Catchon TV",
        source: "Fazier"
      }
    },
    {
      id: 6,
      icon: DollarSign,
      title: "$19.99/month",
      description: "Less Than Your Daily Coffee",
      image: "/images/screenshots/seozast-pricing.png",
      alt: "Seozast pricing plan overview",
      testimonial: {
        quote: "Thanks for introducing such a product at a coffee price. Great product with a best price. You can't go wrong with this.",
        author: "Sudo GreetsU & NikitaY",
        source: "Fazier & Uneed"
      }
    }
  ];

  return (
    <section id="whatis" className="py-6 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Why Choose Seozast?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Real outcomes from real users. Not just features — actual results you can achieve.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => {
            const IconComponent = feature.icon;
            const isHovered = hoveredCard === feature.id;

            return (
              <div
                key={feature.id}
                className={`relative transition-all duration-500 ease-out ${isHovered ? 'z-20' : 'z-10'}`}
                onMouseEnter={() => setHoveredCard(feature.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className={`bg-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-500 cursor-pointer relative overflow-hidden p-8 flex flex-col
                    md:min-h-[450px] md:h-full
                    ${isHovered ? 'hidden md:block md:w-[110%] md:p-10 md:scale-115' : 'w-full scale-100'}`}
                  style={{
                    transformOrigin: 'center',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-2">
                    <IconComponent className="w-6 h-6 text-emerald-600" /> 
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>

                  <div className="relative rounded-lg overflow-hidden">
                    <Image
                      src={feature.image}
                      alt={feature.alt}
                      width={600}
                      height={isHovered ? 288 : 160}
                      className="w-full object-cover rounded-lg transition-all duration-500"
                      style={{ imageRendering: 'auto' }}
                    />
                  </div>

                  {/* Testimonial específico para cada feature */}

                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <div className="flex items-start gap-2 mb-2">
                      {/* <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold text-xs flex-shrink-0">
                       {feature.testimonial.author[0]}
                      </div>  */}
                      <div className="flex-1 min-w-0">

                        <div className="px-6 pt-4">
                          {isHovered ? (
                            <p className="text-xs text-gray-600 italic">
                              &quot;{feature.testimonial.quote}&quot;
                            </p>
                          ) : (
                            <p className="text-xs text-gray-600 italic line-clamp-2">
                              &quot;{feature.testimonial.quote}&quot;
                            </p>
                          )}
                          <p className="text-xs text-emerald-600">
                            {feature.testimonial.source}
                          </p>

                          <p className="text-xs text-gray-800 font-medium mt-1">
                            — {feature.testimonial.author}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Link
            href="/register"
            className="inline-block bg-emerald-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-emerald-700 transition duration-300"
          >
            Try Seozast Free
          </Link>
          <Link
            href="/#pricing"
            className="ml-2 inline-block font-semibold py-3 px-6 rounded-lg text-emerald-600 border border-emerald-500 hover:bg-emerald-50 transition duration-300"
          >
            View pricing
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Whatis;

// 'use client';

// import React, { useState } from 'react';
// import { Search, Brain, Upload, FileText, Link2, DollarSign, ArrowRight } from "lucide-react";
// import Image from 'next/image';
// import Link from 'next/link';

// const Whatis = () => {
//   const [hoveredCard, setHoveredCard] = useState<number | null>(null);

//   const features = [
//     {
//       id: 1,
//       icon: Search,
//       title: "Real Web Analysis",
//       description: "Stop Guessing Keywords — Get Real Competitor Data in Seconds",
//       image: "/images/screenshots/seozast-analysis.png",
//       alt: "Seozast keyword analysis dashboard",
//     },
//     {
//       id: 2,
//       icon: Brain,
//       title: "AI Search Optimization",
//       description: "Get Found by ChatGPT, Copilot & Perplexity Users (Auto-indexed in Bing)",
//       image: "/images/screenshots/seozast-ai-optimization.png",
//       alt: "Seozast AI optimization interface",
//     },
//     {
//       id: 3,
//       icon: FileText,
//       title: "Real-Time Analytics",
//       description: "See Who's Reading Your Content Right Now",
//       image: "/images/screenshots/seozast-analytics.png",
//       alt: "Seozast real-time analytics dashboard",
//     },
//     {
//       id: 4,
//       icon: Upload,
//       title: "One-Click Publishing & Formats",
//       description: "Deploy to GitHub or download in Markdown, Word, HTML, or Next.js TSX/JSX components.",
//       image: "/images/screenshots/seozast-publishing.png",
//       alt: "Seozast publishing and export formats interface",
//     },
//     {
//       id: 5,
//       icon: Link2,
//       title: "Built-in Backlinks",
//       description: "Every article is internally linked and optimized to build authority across your domain, no manual SEO hacks needed.",
//       image: "/images/screenshots/seozast-backlinks.png",
//       alt: "Seozast backlink optimization interface",
//     },
//     {
//       id: 6,
//       icon: DollarSign,
//       title: "Affordable Pricing",
//       description: "Full SEO, AI search optimization and analytics for just $5.99/month. Less than your daily coffee.",
//       image: "/images/screenshots/seozast-pricing.png",
//       alt: "Seozast pricing plan overview",
//     }
//   ];

//   return (
//     <section id="whatis" className="py-10 bg-gray-50">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <div className="text-center mb-16">
//           <h2 className="text-4xl font-bold text-gray-900 mb-4">
//             Why Choose Seozast?
//           </h2>
//           <p className="text-xl text-gray-600 max-w-2xl mx-auto">
//             The only tool that optimizes for both traditional search engines and AI platforms
//           </p>
//         </div>

//         <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
//           {features.map((feature) => {
//             const IconComponent = feature.icon;
//             const isHovered = hoveredCard === feature.id;

//             return (
//               <div
//                 key={feature.id}
//                 className={`relative transition-all duration-500 ease-out ${isHovered ? 'z-20' : 'z-10'}`}
//                 onMouseEnter={() => setHoveredCard(feature.id)}
//                 onMouseLeave={() => setHoveredCard(null)}
//               >
//                 <div
//                   className={`bg-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-500 cursor-pointer relative overflow-hidden p-8 flex flex-col
//                     md:min-h-[450px] md:h-full
//                     ${isHovered ? 'hidden md:block md:w-[110%] md:p-10 md:scale-115' : 'w-full scale-100'}`}
//                   style={{
//                     transformOrigin: 'center',
//                     backfaceVisibility: 'hidden'
//                   }}
//                 >
//                   <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
//                     <IconComponent className="w-6 h-6 text-emerald-600" />
//                   </div>
//                   <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
//                   <p className="text-gray-600 mb-4">{feature.description}</p>

//                   <div className="relative rounded-lg overflow-hidden mb-4">
//                     <Image
//                       src={feature.image}
//                       alt={feature.alt}
//                       width={600}
//                       height={isHovered ? 288 : 160}
//                       className="w-full object-cover rounded-lg transition-all duration-500"
//                       style={{ imageRendering: 'auto' }}
//                     />
//                   </div>
//                 </div>
//               </div>
//             );
//           })}
//         </div>

//         <div className="mt-12 text-center">
//           <Link
//             href="/register"
//             className="inline-block bg-emerald-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-emerald-700 transition duration-300"
//           >
//             Try Seozast Free
//           </Link>
//           <Link
//             href="/#pricing"
//             className="ml-2 inline-block font-semibold py-3 px-6 rounded-lg text-emerald-600 border border-emerald-500 hover:bg-emerald-50 transition duration-300"
//           >
//             View pricing
//           </Link>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default Whatis;
